import { useMemo, useState } from 'react'
import Dropzone from 'react-dropzone'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Stack,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { Z_INDEX } from 'common/constants'
import FileViewer, { FileViewTheme } from 'molecules/FileViewer/FileViewer'
import Loading from 'atoms/Loading'
import ErrorPanel from 'molecules/ErrorPanel'
import MUIDataModelSelector from 'common/components/selectors/MUIDataModelSelector'
import MUIContentTypeSelector from 'common/components/selectors/MUIContentTypeSelector'
import { TranslationMap, TranslationMapLoaderEnum } from 'common/types/kraken-core/TranslationMap'

import { ImportModalProps } from '../types'
import ImportModalConfigurationCards from './ImportModalConfigurationCards'
import { useImportModalContext } from '../contexts/ImportModalContext'
import { DataModelSchemaList } from 'common/types/kraken-core/DataModelSchema'

const ImportModal = ({ onClose, open, mode }: ImportModalProps) => {
  const {
    isOpen,
    mode: contextMode,
    currentStep,
    importedData,
    viewMode,
    validations,
    isImporting,
    isCreating,
    importError,
    createError,
    importResult,
    createResult,
    closeModal,
    setCurrentStep,
    setViewMode,
    handleFileUpload,
    handleImport,
    onUpdate,
    navigateToCreated,
  } = useImportModalContext()

  // Local state for step 1 form fields
  const [translationMapName, setTranslationMapName] = useState('')
  const [direction, setDirection] = useState<'inbound' | 'outbound'>('inbound')
  const [inputDataModel, setInputDataModel] = useState<DataModelSchemaList>(null)
  const [outputDataModel, setOutputDataModel] = useState<DataModelSchemaList>(null)
  const [inputContentType, setInputContentType] = useState<any>(null)
  const [outputContentType, setOutputContentType] = useState<any>(null)
  const [useDataModels, setUseDataModels] = useState(true)

  const handleClose = () => {
    closeModal()
    onClose()
  }

  // Always use context values when available, ignore props
  const modalOpen = isOpen
  const modalMode = contextMode

  const steps: string[] = useMemo(
    () => (modalMode === 'create' ? ['Translation Map', 'Finish'] : ['Upload', 'Verify', 'Finish']),
    [modalMode]
  )

  const getStepIndex = (stepId: string): number => {
    if (modalMode === 'create') {
      return stepId === 'upload-step' ? 0 : 1
    } else {
      switch (stepId) {
        case 'upload-step':
          return 0
        case 'verify-step':
          return 1
        case 'finish-step':
          return 2
        default:
          return 0
      }
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 'upload-step':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              {modalMode === 'create'
                ? 'Create Translation Map'
                : 'Upload your Translation Map settings JSON file'}
            </Typography>

            {/* Translation Map Basic Information */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Translation Map Name"
                  value={translationMapName}
                  onChange={e => setTranslationMapName(e.target.value)}
                  required
                  helperText="Enter a unique name for the translation map"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Direction</InputLabel>
                  <Select
                    value={direction}
                    onChange={e => setDirection(e.target.value as 'inbound' | 'outbound')}
                    label="Direction"
                  >
                    <MenuItem value="inbound">Inbound</MenuItem>
                    <MenuItem value="outbound">Outbound</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Data Model vs Content Type Selection */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Data Configuration
              </Typography>
              <RadioGroup
                value={useDataModels}
                onChange={e => setUseDataModels(e.target.value === 'true')}
                row
              >
                <FormControlLabel value={true} control={<Radio />} label="Use Data Models" />
                <FormControlLabel value={false} control={<Radio />} label="Define Content Types" />
              </RadioGroup>
            </Box>

            {/* Data Model or Content Type Configuration */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Input Data
                </Typography>
                {useDataModels ? (
                  <MUIDataModelSelector
                    selected={{
                      label: inputDataModel?.description,
                      id: inputDataModel?.id,
                    }}
                    onSelect={dataModel => setInputDataModel(dataModel)}
                    label="Input Data Model"
                    placeholder="Search and select input data model..."
                    required
                  />
                ) : (
                  <MUIContentTypeSelector
                    selected={{
                      value: inputContentType?.content_type,
                      label: inputContentType?.description,
                    }}
                    onSelect={contentType => setInputContentType(contentType)}
                    label="Input Content Type"
                    placeholder="Search and select input content type..."
                    required
                  />
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Output Data
                </Typography>

                {useDataModels ? (
                  <MUIDataModelSelector
                    selected={{
                      label: outputDataModel?.description,
                      id: outputDataModel?.id,
                    }}
                    onSelect={dataModel => setOutputDataModel(dataModel)}
                    label="Output Data Model"
                    placeholder="Search and select output data model..."
                    required
                  />
                ) : (
                  <MUIContentTypeSelector
                    selected={{
                      value: outputContentType?.content_type,
                      label: outputContentType?.description,
                    }}
                    onSelect={contentType => setOutputContentType(contentType)}
                    label="Output Content Type"
                    placeholder="Search and select output content type..."
                    required
                  />
                )}
              </Grid>
            </Grid>

            {/* File Upload Section (only for import mode) */}
            {modalMode === 'import' && (
              <>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Upload Translation Map File
                </Typography>
                <Dropzone onDrop={handleFileUpload} accept="application/json">
                  {({ getRootProps, getInputProps }) => (
                    <Paper
                      {...getRootProps()}
                      sx={{
                        p: 3,
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: 'grey.50',
                        '&:hover': {
                          backgroundColor: 'grey.100',
                        },
                      }}
                    >
                      <input {...getInputProps()} />
                      <Typography variant="body1">
                        Drag n drop some files here, or click to select files
                      </Typography>
                    </Paper>
                  )}
                </Dropzone>

                {importedData && (
                  <Box sx={{ mt: 2 }}>
                    <FileViewer
                      options={{
                        readonly: true,
                        heigth: 200,
                        theme: FileViewTheme.DEFAULT,
                      }}
                      content={JSON.stringify(importedData, null, 2)}
                      contentType="application/json"
                      showOptions={false}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        )

      case 'verify-step':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {modalMode === 'create'
                ? 'Review Translation Map Configuration'
                : 'Review Imported Data'}
            </Typography>

            {(importedData || (modalMode === 'create' && translationMapName)) && (
              <Box sx={{ mb: 2 }}>
                <ImportModalConfigurationCards
                  translationMap={importedData || (createTranslationMapData() as any)}
                  viewMode={viewMode}
                  onUpdate={onUpdate}
                />
              </Box>
            )}

            {validations && <ErrorPanel error={validations} />}
          </Box>
        )

      case 'finish-step':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {(isImporting || isCreating) && <Loading className="mt-8 justify-center" />}
            {(importError || createError) && (
              <ErrorPanel
                error={
                  (importError || createError)?.message || 'An error occurred during the operation'
                }
              />
            )}
            {(importResult || createResult) && (
              <Alert
                severity="success"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      const data = importResult || createResult
                      if (data?.id) {
                        closeModal()
                        navigateToCreated(data.id)
                      }
                    }}
                  >
                    Click here to see
                  </Button>
                }
                sx={{ mb: 2 }}
              >
                Translation Map successfully {modalMode === 'create' ? 'created' : 'imported'}.
              </Alert>
            )}
          </Box>
        )

      default:
        return null
    }
  }

  // Validation function for step 1
  const isStep1Valid = () => {
    if (!translationMapName.trim()) return false
    if (!direction) return false

    if (useDataModels) {
      return !!(inputDataModel && outputDataModel)
    } else {
      return !!(inputContentType && outputContentType)
    }
  }

  // Create translation map data from form
  const createTranslationMapData = (): Partial<TranslationMap> => {
    const baseData: Partial<TranslationMap> = {
      name: translationMapName,
      use_when_direction: direction,
      output_name_pattern: 'payload.json',
      loader: TranslationMapLoaderEnum.RUNTIME_DATABASE_LOADER,
      output_encoding: 'UTF-8',
      input_encoding: 'UTF-8',
    }

    if (useDataModels) {
      return {
        ...baseData,
        input_data_model: inputDataModel?.id,
        output_data_model: outputDataModel?.id,
        output_content_type: outputDataModel?.engine,
        input_content_type: inputDataModel?.engine,
      }
    } else {
      return {
        ...baseData,
        input_content_type: inputContentType?.content_type,
        output_content_type: outputContentType?.content_type,
      }
    }
  }

  const renderStepActions = () => {
    switch (currentStep) {
      case 'upload-step':
        return (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              disabled={modalMode === 'import' ? !importedData : !isStep1Valid()}
              onClick={() => {
                if (modalMode === 'create') {
                  // For create mode, pass the form data to the next step
                  const translationMapData = createTranslationMapData()
                  onUpdate(translationMapData as TranslationMap)
                }
                setCurrentStep('verify-step')
              }}
            >
              Next
            </Button>
          </Stack>
        )

      case 'verify-step':
        return (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={modalMode === 'create' && !isStep1Valid()}
            >
              {modalMode === 'import' && 'Import'}
              {modalMode === 'create' && 'Create'}
            </Button>
          </Stack>
        )

      case 'finish-step':
        return (
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={handleClose}>Close</Button>
          </Stack>
        )

      default:
        return null
    }
  }

  return (
    <Dialog
      open={modalOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          zIndex: Z_INDEX.DIALOG,
        },
      }}
    >
      <DialogTitle>
        {modalMode === 'create' ? 'Create Translation Map' : 'Import Translation Map'}
      </DialogTitle>
      <DialogContent sx={{ maxHeight: '70vh', overflow: 'auto' }}>
        <Box sx={{ mt: 2 }}>
          <Stepper activeStep={getStepIndex(currentStep)} sx={{ mb: 2 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {renderStepContent()}
        </Box>
      </DialogContent>
      <DialogActions>{renderStepActions()}</DialogActions>
    </Dialog>
  )
}

export default ImportModal
